/**
 * HỆ XƯƠNG CUTOUT — tự viết, không cần Spine/Rive.
 *
 * Vì sao tự viết: mỗi phụ kiện chỉ là 1 MẢNH gắn vào 1 XƯƠNG có sẵn, nên:
 *   - 1 ảnh nhân vật ⇒ 8 hoạt ảnh (không vẽ từng frame)
 *   - 1 cái mũ ⇒ 1 mảnh (không phải 8 frame × 7 cấp)
 *
 * Ma trận 2×3: [a c e]
 *              [b d f]
 * Đây là logic thuần, KHÔNG import Phaser ⇒ test được bằng unit test.
 */

export interface Mat {
  a: number; b: number; c: number; d: number; e: number; f: number;
}

export const IDENTITY: Mat = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

export function matMul(m: Mat, n: Mat): Mat {
  return {
    a: m.a * n.a + m.c * n.b,
    b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d,
    d: m.b * n.c + m.d * n.d,
    e: m.a * n.e + m.c * n.f + m.e,
    f: m.b * n.e + m.d * n.f + m.f,
  };
}

export function matApply(m: Mat, x: number, y: number): { x: number; y: number } {
  return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f };
}

export function matRotation(m: Mat): number {
  return Math.atan2(m.b, m.a);
}

export function matScale(m: Mat): { x: number; y: number } {
  return { x: Math.hypot(m.a, m.b), y: Math.hypot(m.c, m.d) };
}

export class Bone {
  world: Mat = { ...IDENTITY };
  /** toạ độ gốc xương trong hệ của xương cha */
  x = 0;
  y = 0;
  rot = 0;
  sx = 1;
  sy = 1;
  constructor(public name: string, public parent: Bone | null) {}

  local(): Mat {
    const cos = Math.cos(this.rot);
    const sin = Math.sin(this.rot);
    return {
      a: cos * this.sx,
      b: sin * this.sx,
      c: -sin * this.sy,
      d: cos * this.sy,
      e: this.x,
      f: this.y,
    };
  }
}

export interface PartInstance {
  bone: string;
  /** khoá texture (do lớp art sinh ra) */
  key: string;
  /** lệch so với gốc xương */
  ox: number;
  oy: number;
  /** thứ tự vẽ — nhỏ vẽ trước */
  z: number;
  sx: number;
  sy: number;
  /** lật ngang khi nhân vật quay mặt sang trái */
  flip?: boolean;
  /** khoá slot phụ kiện (để đổi đồ lúc chạy) */
  slot?: string;
}

export interface ResolvedPart {
  part: PartInstance;
  x: number;
  y: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
  z: number;
}

export class Rig {
  bones = new Map<string, Bone>();
  /** thứ tự topo — cha trước con */
  private order: Bone[] = [];
  parts: PartInstance[] = [];
  /** điểm neo của cả nhân vật (bàn chân) */
  anchorY = 0;
  /** hệ số co đã áp lên rig (art AI nạp ở kích thước gốc to hơn nhiều) */
  nativeScale = 1;

  addBone(name: string, parent: string | null, x = 0, y = 0): Bone {
    const p = parent ? (this.bones.get(parent) ?? null) : null;
    if (parent && !p) throw new Error(`Không tìm thấy xương cha: ${parent}`);
    const b = new Bone(name, p);
    b.x = x;
    b.y = y;
    this.bones.set(name, b);
    this.order.push(b);
    return b;
  }

  addPart(part: PartInstance): void {
    if (!this.bones.has(part.bone)) throw new Error(`Mảnh gắn vào xương không tồn tại: ${part.bone}`);
    this.parts.push(part);
    this.parts.sort((a, b) => a.z - b.z);
  }

  /** Đổi phụ kiện trong 1 slot mà KHÔNG cần dựng lại rig. */
  replaceSlot(slot: string, next: PartInstance[]): void {
    this.parts = this.parts.filter((p) => p.slot !== slot);
    for (const p of next) this.parts.push(p);
    this.parts.sort((a, b) => a.z - b.z);
  }

  bone(name: string): Bone {
    const b = this.bones.get(name);
    if (!b) throw new Error(`Không có xương: ${name}`);
    return b;
  }

  /** Tra xương KHÔNG ném lỗi — để pose dùng chung cho cả nhân vật lẫn quái. */
  tryBone(name: string): Bone | undefined {
    return this.bones.get(name);
  }

  hasBone(name: string): boolean {
    return this.bones.has(name);
  }

  resetPose(): void {
    for (const b of this.order) {
      b.rot = 0;
      b.sx = 1;
      b.sy = 1;
    }
  }

  /** Tính ma trận thế giới cho mọi xương, rồi suy ra transform của từng mảnh. */
  update(): ResolvedPart[] {
    for (const b of this.order) {
      const local = b.local();
      b.world = b.parent ? matMul(b.parent.world, local) : local;
    }
    const out: ResolvedPart[] = [];
    for (const p of this.parts) {
      const bone = this.bones.get(p.bone)!;
      const pos = matApply(bone.world, p.ox, p.oy);
      const sc = matScale(bone.world);
      out.push({
        part: p,
        x: pos.x,
        y: pos.y,
        rotation: matRotation(bone.world),
        scaleX: sc.x * p.sx,
        scaleY: sc.y * p.sy,
        z: p.z,
      });
    }
    return out;
  }
}
